/**
 * @format
 */
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    inject,
    Input,
    OnInit,
    Output,
    ViewEncapsulation,
} from '@angular/core';
import { PlanValidationResult } from 'common-typescript';
import {
    CourseUnit,
    CustomCourseUnitAttainment,
    CustomModuleAttainment,
    CustomStudyDraft,
    EntityWithRule,
    Module,
    ModuleContentApplication,
    ModuleContentWorkflow,
    StudyRight,
} from 'common-typescript/types';
import { compact, filter, flatMap, get, includes, isEmpty, keys, map, some, values } from 'lodash-es';
import { take } from 'rxjs';
import {
    AppErrorHandler,
    PLAN_ACTIONS_SERVICE_INJECTION_TOKEN,
    PlanActionsService,
    PlanStateObject,
    PlanStructureContext,
} from 'sis-components';
import { applyForModuleContentWorkflowDialogOpener } from '../../../../../plan-ng/apply-for-module-content-workflow-dialog/apply-for-module-content-workflow-dialog.component';
import { ShowViewModuleContentWorkflowLinkService } from '../../../show-view-module-content-application-link/show-view-module-content-workflow-link.service';

@Component({
    selector: 'app-plan-structure-edit-invalid-selections',
    templateUrl: './plan-structure-edit-invalid-selections.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    standalone: false,
})
export class PlanStructureEditInvalidSelectionsComponent implements OnInit {
    private readonly planActionsService: PlanActionsService = inject(PLAN_ACTIONS_SERVICE_INJECTION_TOKEN);
    private readonly showViewModuleContentWorkflowLinkService: ShowViewModuleContentWorkflowLinkService = inject(
        ShowViewModuleContentWorkflowLinkService,
    );
    private readonly appErrorHandler: AppErrorHandler = inject(AppErrorHandler);
    private readonly changeDetectorRef = inject(ChangeDetectorRef);
    protected readonly planStructureContext = inject(PlanStructureContext);

    hasInvalidSelections = false;

    @Input() parentModule: EntityWithRule;
    @Input() planStateObject: PlanStateObject;
    @Input() planValidationResult: PlanValidationResult;
    @Input() headingLevel: number;
    @Input() moduleContentApplications: ModuleContentApplication[];
    @Input() allStudentStudyRights: StudyRight[];
    @Input() matchingStudyRight: StudyRight;
    @Output() workflowCreated = new EventEmitter<ModuleContentWorkflow>();

    invalidCourseUnits: CourseUnit[];
    invalidCustomCourseUnitAttainments: CustomCourseUnitAttainment[];
    invalidModules: Module[];
    invalidCustomModuleAttainments: CustomModuleAttainment[];
    invalidCustomStudyDrafts: CustomStudyDraft[];

    private readonly applyForModuleContentWorkflowDialogOpener = applyForModuleContentWorkflowDialogOpener();

    ngOnInit() {
        this.invalidCourseUnits = filter(
            this.planStructureContext.validatablePlan().getSelectedCourseUnitsUnderModule(this.parentModule),
            (courseUnit: CourseUnit) => get(this.planStateObject.courseUnits, [courseUnit.id, 'invalidSelection']) === true,
        );
        this.invalidCustomCourseUnitAttainments = filter(
            this.planStructureContext.validatablePlan().getSelectedCustomCourseUnitAttainmentsUnderModule(this.parentModule),
            (ccua: CustomCourseUnitAttainment) =>
                get(this.planStateObject.customCourseUnitAttainments, [ccua.id, 'invalidSelection']) === true,
        );
        this.invalidModules = filter(
            this.planStructureContext.validatablePlan().getSelectedModulesUnderModule(this.parentModule),
            (module: Module) => get(this.planStateObject.modules, [module.id, 'invalidSelection']) === true,
        );
        this.invalidCustomModuleAttainments = filter(
            this.planStructureContext.validatablePlan().getSelectedCustomModuleAttainmentsUnderModule(this.parentModule),
            (cma: CustomModuleAttainment) => get(this.planStateObject.customModuleAttainments, [cma.id, 'invalidSelection']) === true,
        );
        this.invalidCustomStudyDrafts = this.getInvalidCustomStudyDrafts();

        const allInvalidStudyArrays = [
            this.invalidCourseUnits,
            this.invalidCustomCourseUnitAttainments,
            this.invalidModules,
            this.invalidCustomModuleAttainments,
            this.invalidCustomStudyDrafts,
        ];

        // Check if there is any application for this parentModule in a relevant state
        const hasRelevantApplication = this.moduleContentApplications?.some(
            app =>
                app.approvedModuleId === this.parentModule?.id &&
                (app.state === 'ACCEPTED' || app.state === 'CONDITIONAL' || app.state === 'REQUESTED' || app.state === 'IN_HANDLING'),
        );

        if (some(allInvalidStudyArrays, array => !isEmpty(array)) && !hasRelevantApplication) {
            this.hasInvalidSelections = true;
        }
    }

    handleCourseUnitToggle(courseUnit: CourseUnit) {
        if (this.planStructureContext.validatablePlan().isCourseUnitInPlan(courseUnit)) {
            this.planActionsService?.removeCourseUnit(courseUnit, this.parentModule);
        } else {
            this.planActionsService?.forceSelectCourseUnitById(courseUnit.id, this.parentModule);
        }
    }

    handleCustomCourseUnitAttainmentToggle(customCourseUnitAttainment: CustomCourseUnitAttainment) {
        if (this.planStructureContext.validatablePlan().isCustomCourseUnitAttainmentInPlan(customCourseUnitAttainment)) {
            this.planActionsService?.removeCustomCourseUnitAttainmentById(customCourseUnitAttainment.id, this.parentModule);
        } else {
            this.planActionsService?.selectCustomCourseUnitAttainment(customCourseUnitAttainment, this.parentModule);
        }
    }

    handleCustomStudyDraftToggle(customStudyDraft: CustomStudyDraft) {
        if (this.isCustomStudyDraftInPlan(customStudyDraft)) {
            this.planActionsService?.removeCustomStudyDraft(customStudyDraft, this.parentModule);
        } else {
            this.planActionsService?.addCustomStudyDraft(customStudyDraft, this.parentModule);
        }
    }

    handleModuleToggle(module: Module) {
        if (this.planStructureContext.validatablePlan().isModuleInPlan(module.id)) {
            this.planActionsService?.removeModule(module, this.parentModule);
        } else {
            this.planActionsService?.forceSelectModuleById(module.id, this.parentModule);
        }
    }

    handleCustomModuleAttainmentToggle(customModuleAttainment: CustomModuleAttainment) {
        if (this.planStructureContext.validatablePlan().isModuleAttainmentInPlan(customModuleAttainment.id)) {
            this.planActionsService?.removeCustomModuleAttainmentById(customModuleAttainment.id, this.parentModule);
        } else {
            this.planActionsService?.selectCustomModuleAttainment(customModuleAttainment, this.parentModule);
        }
    }

    isCustomStudyDraftInPlan(customStudyDraft: CustomStudyDraft) {
        return includes(
            map(this.planStructureContext.validatablePlan().getSelectedCustomStudyDraftsByParentModuleId(this.parentModule.id), 'id'),
            customStudyDraft.id,
        );
    }

    getInvalidCustomStudyDrafts() {
        const ruleValidationResultsForModule = values(get(this.planValidationResult?.ruleValidationResults, this.parentModule.id));
        const matchedCustomStudyDraftIds = flatMap(compact(flatMap(ruleValidationResultsForModule, 'selectedCustomStudyDraftsById')), keys);
        const unmatchedCustomStudyDrafts = filter(
            this.planStructureContext.validatablePlan().getSelectedCustomStudyDraftsByParentModuleId(this.parentModule.id),
            csd => !includes(matchedCustomStudyDraftIds, csd.id),
        );
        return unmatchedCustomStudyDrafts;
    }

    openApplyForModuleContentWorkflowDialog(): void {
        if (!this.planHasActiveStudyRight()) {
            return;
        }
        this.applyForModuleContentWorkflowDialogOpener({
            approvalRequestType: this.showViewModuleContentWorkflowLinkService.isModuleContentApplicationRequired(
                this.parentModule as Module,
            )
                ? 'REQUIRED'
                : 'CUSTOM',
            module: this.parentModule as Module,
            validatablePlan: this.planStructureContext.validatablePlan(),
            matchingStudyRight: this.matchingStudyRight,
            planData: this.planStructureContext.planData(),
            planStateObject: this.planStateObject,
        })
            .afterClosed()
            .pipe(take(1), this.appErrorHandler.defaultErrorHandler())
            .subscribe((workflow: ModuleContentWorkflow) => {
                if (workflow) {
                    this.workflowCreated.next(workflow);
                }
            });
    }

    isModuleContentWorkflowRequired() {
        return this.showViewModuleContentWorkflowLinkService.isModuleContentApplicationRequired(this.parentModule as Module);
    }

    planHasActiveStudyRight(): boolean {
        return this.showViewModuleContentWorkflowLinkService.planHasActiveStudyRight(
            this.planStructureContext.validatablePlan(),
            this.allStudentStudyRights,
        );
    }

    hasModuleContentApplication(): boolean {
        const moduleContentApplication = this.planStructureContext
            .validatablePlan()
            .moduleContentApplications?.findLast((app: ModuleContentApplication) => app.approvedModuleId === this.parentModule?.id);
        return (
            moduleContentApplication?.state === 'ACCEPTED' ||
            moduleContentApplication?.state === 'CONDITIONAL' ||
            moduleContentApplication?.state === 'REQUESTED' ||
            moduleContentApplication?.state === 'IN_HANDLING'
        );
    }

    refresh(): void {
        this.changeDetectorRef.detectChanges();
    }
}
